import React from 'react';
import {
  Building2, ShieldCheck, Home, MessageCircle, MapPin,
  Search, UserCheck, KeyRound,
} from 'lucide-react';
import HeaderSSR from '../../components/HeaderSSR';
import FooterSSR from '../../components/FooterSSR';

// ============================================================
// STATS DATA
// ============================================================

const stats = [
  { value: '450+', label: 'Neighborhoods' },
  { value: '38', label: 'Cities & Towns' },
  { value: 'Verified', label: 'Agents' },
  { value: '47', label: 'Counties' },
];

// ============================================================
// VALUE CARDS DATA
// ============================================================

const valueCards = [
  {
    icon: ShieldCheck,
    title: 'Verified Agents',
    description:
      'Every agent on our platform is verified to ensure you work with trusted professionals.',
  },
  {
    icon: Home,
    title: 'Quality Listings',
    description:
      'Browse curated property listings with detailed information and high-quality images.',
  },
  {
    icon: MessageCircle,
    title: 'Direct Connection',
    description:
      'Connect directly with agents through our messaging system for quick responses.',
  },
];

// ============================================================
// HOW IT WORKS DATA
// ============================================================

const steps = [
  {
    number: 1,
    icon: Search,
    title: 'Browse',
    description:
      'Search verified listings across Kenya by location, price, and type.',
  },
  {
    number: 2,
    icon: UserCheck,
    title: 'Connect',
    description:
      'Message verified agents directly through our secure platform.',
  },
  {
    number: 3,
    icon: KeyRound,
    title: 'Move In',
    description:
      'Complete your purchase with confidence knowing every agent is verified.',
  },
];

// ============================================================
// CITIES DATA
// ============================================================

const cities = [
  'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika',
  'Malindi', 'Naivasha', 'Nanyuki', 'Diani', 'Lamu', 'Nyeri',
];

// ============================================================
// PROPERTY TYPES DATA
// ============================================================

const propertyTypes = [
  'Apartments and Flats',
  'Houses and Villas',
  'Land and Plots',
  'Commercial Properties',
  'Townhouses',
  'Bedsitters and Studios',
];

// ============================================================
// MAIN ABOUT PAGE
// ============================================================

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderSSR />

      {/* Hero Section */}
      <header className="pt-24 pb-12 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-6">
            <Building2 className="w-8 h-8 text-green-700" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            About Afriqahome
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Kenya's trusted marketplace for verified real estate
          </p>
        </div>
      </header>

      {/* Stats Bar */}
      <section className="px-4 -mt-2 mb-12">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((stat) => (
              <div key={stat.label}>
                <p className="text-2xl font-bold text-green-700">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <main>
        {/* Mission Section */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Afriqahome connects property seekers with verified real estate agents across Kenya.
            In a market where property scams cost buyers millions every year, we believe everyone
            deserves access to trustworthy listings and professional agents who are verified before
            they can list.
          </p>
        </section>

        {/* What Sets Us Apart */}
        <section className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            What Sets Us Apart
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {valueCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="bg-white rounded-2xl p-6 border border-gray-100"
                >
                  <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-green-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {card.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {card.description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">How It Works</h2>
            <p className="text-gray-600">
              Find your next property in three simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Dashed connector line — desktop only */}
            <div className="hidden md:block absolute top-5 left-[calc(16.67%+20px)] right-[calc(16.67%+20px)] border-t-2 border-dashed border-gray-200" />

            {steps.map((step) => (
              <div key={step.number} className="text-center relative">
                <div className="w-10 h-10 bg-green-700 text-white rounded-full font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {step.number}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                <p className="text-sm text-gray-500">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Coverage Section */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Coverage</h2>
          <div className="flex flex-wrap gap-3 mb-4">
            {cities.map((city) => (
              <span
                key={city}
                className="inline-flex items-center bg-green-50 text-green-800 rounded-full px-4 py-2 text-sm font-medium"
              >
                <MapPin className="w-4 h-4 mr-1.5" />
                {city}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-400">
            Covering 38 cities and 450+ neighborhoods across Kenya
          </p>
        </section>

        {/* Property Types */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Types</h2>
          <p className="text-lg text-gray-600 mb-6">
            Whether you're looking to buy, rent, or invest, we have listings for:
          </p>
          <ul className="grid md:grid-cols-2 gap-3">
            {propertyTypes.map((type) => (
              <li key={type} className="flex items-center text-gray-700">
                <span className="w-2 h-2 bg-green-600 rounded-full mr-3 flex-shrink-0" />
                {type}
              </li>
            ))}
          </ul>
        </section>

        {/* Dual CTA Section */}
        <section className="max-w-5xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-2 gap-6">
            {/* For Buyers */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Looking for a home?
              </h3>
              <p className="text-gray-600 mb-6">
                Browse verified properties across Kenya
              </p>
              <a
                href="/"
                className="inline-block px-6 py-3 bg-green-700 text-white font-semibold rounded-xl hover:bg-green-800 transition-colors"
              >
                Browse Properties
              </a>
            </div>

            {/* For Agents */}
            <div className="bg-green-700 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-2">
                Are you a real estate agent?
              </h3>
              <p className="text-green-100 mb-6">
                Join Kenya's fastest-growing verified marketplace
              </p>
              <a
                href="/pricing"
                className="inline-block px-6 py-3 bg-white text-green-700 font-semibold rounded-xl hover:bg-green-50 transition-colors"
              >
                View Agent Plans
              </a>
            </div>
          </div>
        </section>
      </main>

      <FooterSSR />
    </div>
  );
}
